<template>
  <div class="weather-page">
    <v-card class="weather-card rounded-xl pa-4 d-flex flex-column ops-basic-bg" variant="flat" :loading="loading">
      <!-- 顶部筛选 -->
      <div class="toolbar-top mb-4 flex-shrink-0">
        <div class="toolbar-main d-flex flex-wrap align-center ga-4">
          <div class="search-item">
            <span class="search-label">日期</span>
            <el-date-picker v-model="queryTimer" type="daterange" :clearable="false" range-separator="~"
              start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" class="search-date-picker"
              @change="handleSearch" />
          </div>
          <div class="search-item">
            <span class="search-label">省市</span>
            <v-select v-model="queryProvince" :items="provinceItems" item-title="title" item-value="value"
              placeholder="全部省市" variant="outlined" density="compact" hide-details clearable
              class="search-input--select" rounded="xl" />
          </div>
          <div class="search-item">
            <span class="search-label">日间温度下限</span>
            <v-text-field v-model.number="queryTemperature" type="number" :min="-50" :max="50" placeholder="不限"
              variant="outlined" density="compact" hide-details clearable class="search-input--number" rounded="xl"
              @keyup.enter="handleSearch" />
          </div>

          <div class="search-actions d-flex ga-3">
            <v-btn color="primary" rounded="xl" class="action-btn" @click="handleSearch">
              <v-icon start icon="mdi-magnify" />查询
            </v-btn>
            <v-btn variant="outlined" color="success" rounded="xl" class="action-btn" @click="resetFilters">
              <v-icon start icon="mdi-refresh" />重置
            </v-btn>
          </div>
        </div>
      </div>

      <!-- 表格：表头固定，仅 tbody 区域随剩余高度滚动 -->
      <div class="table-wrap">
        <div v-if="tableData.length" class="weather-table-host">
          <v-table fixed-header height="100%" class="gm-table weather-table gm-border rounded-lg" density="comfortable">
            <thead>
              <tr>
                <th v-for="column in columns" :key="column.key" :style="{ width: column.width }">{{ column.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in tableData" :key="rowKey(item, idx)">
                <td v-for="column in columns" :key="column.key" :style="{ width: column.width }"
                  :title="column.render(item)">{{ column.render(item) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div v-else class="empty-tip empty-tip--fill text-center py-10 text-medium-emphasis">暂无天气数据</div>
      </div>

      <TablePagination v-model="page" v-model:page-size="pageSize" class="weather-pagination flex-shrink-0"
        :total="total" :has-sizes="true" :has-jump="true" @change="fetchList" />
    </v-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import TablePagination from '@/components/TablePagination/index.vue'
import { regionData } from '@/utils/regionData'
import { useAppStore } from '@/store/modules/app'
import { getWeatherData } from './api.js'

const appStore = useAppStore()

/** 获取 stationId，并在其变化时重新拉取列表（与原模块一致） */
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    fetchList()
  }
})

const DATE_FMT = 'YYYY-MM-DD'
const defaultRange = () => [dayjs().format(DATE_FMT), dayjs().format(DATE_FMT)]

const queryTimer = ref(defaultRange())
const queryProvince = ref('')
const queryTemperature = ref(null)

const provinceItems = computed(() => {
  const list = [{ title: '全部省市', value: '' }]
  for (const p of Object.keys(regionData)) {
    list.push({ title: p, value: p })
  }
  return list
})

const columns = [
  { key: 'weatherDate', label: '日期', width: '16%', render: (row) => formatDate(row.weatherDate) },
  { key: 'place', label: '省市', width: '16%', render: (row) => row.place ?? '--' },
  { key: 'dayAirTemperature', label: '日间气温', width: '14%', render: (row) => row.dayAirTemperature ?? '--' },
  { key: 'nightAirTemperature', label: '夜间气温', width: '14%', render: (row) => row.nightAirTemperature ?? '--' },
  { key: 'dayWeather', label: '天气', width: '14%', render: (row) => row.dayWeather ?? '--' },
  { key: 'ziwaixian', label: '紫外线', width: '13%', render: (row) => row.ziwaixian ?? '--' },
  { key: 'jiangshui', label: '降水量', width: '13%', render: (row) => row.jiangshui ?? '--' }
]

const tableData = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const loading = ref(false)

function rowKey(item, idx) {
  return `${item?.weatherDate ?? ''}-${item?.place ?? ''}-${idx}`
}

function formatDate(date) {
  if (!date) return '--'
  return dayjs(date).format(DATE_FMT)
}

/** 每页条数变化由 TablePagination 重置页码，这里仅负责重新拉取 */
watch(pageSize, () => fetchList())

async function fetchList() {
  loading.value = true
  tableData.value = []
  const params = {
    pageSize: pageSize.value,
    pageNum: page.value,
    province: queryProvince.value || '',
    temperature: queryTemperature.value,
    startDate: dayjs(queryTimer.value?.[0]).format(DATE_FMT),
    endDate: dayjs(queryTimer.value?.[1]).format(DATE_FMT),
    stationId: stationId.value
  }
  try {
    const res = await getWeatherData(params)
    tableData.value = res?.data?.rows ?? []
    total.value = res?.data?.total ?? 0
  } catch (e) {
    console.warn(e)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  page.value = 1
  fetchList()
}

function resetFilters() {
  queryTimer.value = defaultRange()
  queryProvince.value = ''
  queryTemperature.value = null
  handleSearch()
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped lang="scss">
.weather-page {
  display: flex;
  flex-direction: column;
  min-height: 560px;
  height: 100%;
  box-sizing: border-box;
  /* 作为 tab 内容嵌在 .scrollable-content 中，必须允许收缩，否则表格会把父容器撑破 */
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

/* 与策略模板等 tab 页保持一致的卡片底色 */
.ops-basic-bg {
  border-radius: 18px;
  border: 0.5px solid #FFF;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
}

.weather-card {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.toolbar-top {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar-main {
  min-width: 0;
  max-width: 100%;
}

.action-btn {
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  max-width: 100%;
  min-width: 0;

  .search-label {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    text-align: right;
    min-width: 64px;
  }

  .search-input--select {
    width: 180px;
  }

  .search-input--number {
    width: 150px;
  }
}

.search-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  row-gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
}

/* el-date-picker 对齐 Vuetify 输入框的圆角描边风格 */
.search-date-picker {
  width: 260px;
  max-width: 100%;

  :deep(.el-input__wrapper),
  :deep(.el-range-editor.el-input__wrapper) {
    width: 100%;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 999px;
    box-shadow: none;
    padding: 0 16px;
    min-height: 40px;
    height: 40px;

    &.is-active,
    &:hover {
      border-color: #2655f1;
    }
  }
}

.table-wrap {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.weather-table-host {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.weather-table {
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

/* 仅纵向滚动：列宽按百分比自适应，不需要横向滚动 */
.weather-table :deep(.v-table__wrapper) {
  overflow-x: hidden;
  overflow-y: auto;
  max-width: 100%;
}

/* 固定表格布局，7 列按百分比均分容器宽度，不产生横向溢出 */
.weather-table :deep(.v-table__wrapper > table) {
  width: 100%;
  table-layout: fixed;
}

.weather-table :deep(tbody td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weather-table :deep(thead th) {
  background: rgba(255, 255, 255, 0.96) !important;
}

.empty-tip--fill {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.weather-pagination {
  :deep(.table-pagination) {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
